import {IApiPagingInput} from "libraries/service/resources/IApiPagingInput";
import {BookingSearchParameters, IBookingSearchParameters} from "libraries/interfaces/IBookingSearchParameters";
import {IMinMax} from "libraries/interfaces/IMinMax";

export class ApiSearchParameters {
    public recordset: IApiPagingInput = {
        first: 1,
        nbrecord: 20,
    };
    public templateArray: string[] = undefined;

    public booking: IApiBookingSearchParameters = {};

    public static fromSearchParameters(input: IBookingSearchParameters, isCarbon:boolean = false): ApiSearchParameters {
        const result: ApiSearchParameters = new ApiSearchParameters();
        const inputFirst: number = input && input.first || 1;
        if (isCarbon){
            result.templateArray = ["carbon"];
        }
        result.recordset.nbrecord = input && input.nbrecord || 20;
        result.recordset.first = Math.max(1, inputFirst);

        const {
            nbrecord = 20,
            first = 1,
            midOffice = undefined,
            seatStatus = undefined,
            sectorMatchAll = undefined,
            sectorFrom = undefined,
            sectorTo = undefined,
            sectorSeatStatus = undefined,
            sectorFlightNumber = undefined,
            sectorBeginDate = undefined,
            sectorArrivalDate = undefined,
            sectorCarrier = undefined,
            sectorOperatingCarrier = undefined,
            documentTicket = undefined,
            pricingRate = undefined,
            pricingXState = undefined,
            pricingFarebasis = undefined,
            serviceCode = undefined,
            serviceXStatus = undefined,
            remarkText = undefined,
            remarkNote = undefined,
            remarkRemark = undefined,
            remarkInvoicing = undefined,
            remarkOsi = undefined,
            remarkVendorAlert = undefined,
            retentionReason = undefined,
            whenRetentionEnds = undefined,

            ticketMatchAll = undefined,
            ticketFlightNumber = undefined,
            ticketSectorBeginDate = undefined,
            ticketSectorCarrier = undefined,
            ticketSectorFrom = undefined,
            ticketSectorTo = undefined,
            ticketStatus = undefined,
            ticketNativeStatus = undefined,

            pendingValueMin = undefined,
            pendingValueMax = undefined,
            pendingPercentageMin = undefined,
            pendingPercentageMax = undefined,

            paidValueMin = undefined,
            paidValueMax = undefined,
            paidPercentageMin = undefined,
            paidPercentageMax = undefined,

            dueValueMin = undefined,
            dueValueMax = undefined,
            duePercentageMin = undefined,
            duePercentageMax = undefined,

            placesToStay = undefined,
            propertyName = undefined,
            propertyReference = undefined,
            stayBeginDate = undefined,
            stayArrivalDate = undefined,

            qualityCheck = undefined,

            hasFareOptimisation = undefined,
            actionForFareOptimisation = undefined,
            savingMin = undefined,
            impactOnFareType = undefined,
            impactOnLuggage = undefined,

            ...bookingInput
        }: IBookingSearchParameters = input || {};

        result.booking = bookingInput as IApiBookingSearchParameters;

        const hasSectorValue: boolean = [
            // we omit `sectorMatchAll` here because, alone, it does not make sense
            BookingSearchParameters.sectorFrom,
            BookingSearchParameters.sectorTo,
            BookingSearchParameters.sectorSeatStatus,
            BookingSearchParameters.sectorBeginDate,
            BookingSearchParameters.sectorArrivalDate,
            BookingSearchParameters.sectorCarrier,
            BookingSearchParameters.sectorFlightNumber,
            BookingSearchParameters.sectorOperatingCarrier
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]));

        const hasTicketValue: boolean = [
            // we omit `sectorMatchAll` here because, alone, it does not make sense
            BookingSearchParameters.ticketSectorCarrier,
            BookingSearchParameters.ticketSectorBeginDate,
            BookingSearchParameters.ticketMatchAll,
            BookingSearchParameters.ticketFlightNumber,
            BookingSearchParameters.ticketStatus,
            BookingSearchParameters.ticketNativeStatus,
            BookingSearchParameters.ticketSectorFrom,
            BookingSearchParameters.ticketSectorTo
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]));

        const hasPendingValue: boolean = [
            BookingSearchParameters.pendingValueMin,
            BookingSearchParameters.pendingValueMax
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]) || input[propertyName] === 0);

        const hasPercentageValue: boolean = [
            BookingSearchParameters.pendingPercentageMin,
            BookingSearchParameters.pendingPercentageMax
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]) || input[propertyName] === 0);

        const hasPaidValue: boolean = [
            BookingSearchParameters.paidValueMin,
            BookingSearchParameters.paidValueMax
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]) || input[propertyName] === 0);

        const hasPaidPercentage: boolean = [
            BookingSearchParameters.paidPercentageMin,
            BookingSearchParameters.paidPercentageMax
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]) || input[propertyName] === 0);

        const hasDueValue: boolean = [
            BookingSearchParameters.dueValueMin,
            BookingSearchParameters.dueValueMax
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]) || input[propertyName] === 0);

        const hasDuePercentage: boolean = [
            BookingSearchParameters.duePercentageMin,
            BookingSearchParameters.duePercentageMax
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]) || input[propertyName] === 0);


        const hasExternalBookingValue: boolean = ['midOffice']
            .some((propertyName: string): boolean => Boolean(input[propertyName]));

        const hasDocumentValue: boolean = ['documentTicket', 'documentEnd']
            .some((propertyName: string): boolean => Boolean(input[propertyName]));

        const hasPriceValue: boolean = ['pricingRate', 'pricingXState', 'pricingFarebasis']
            .some((propertyName: string): boolean => Boolean(input[propertyName]));

        const hasServiceValue: boolean = ['serviceCode', 'serviceXStatus']
            .some((propertyName: string): boolean => Boolean(input[propertyName]));

        const hasRetentionValue: boolean = Boolean(
            retentionReason || whenRetentionEnds
        );

        const hasRemarkValue: boolean = Boolean(
            remarkText || remarkRemark || remarkNote || remarkInvoicing || remarkOsi || remarkVendorAlert
        );

        const hasPropertyValue: boolean = Boolean(
            placesToStay || propertyName || propertyReference || stayBeginDate || stayArrivalDate
        );

        const hasQualityCheckValue: boolean = Boolean(
            qualityCheck
        );

        if(input.hasMembershipCard?.length){
            const hasMembershipCardValue: boolean = input.hasMembershipCard[0] === 'true';

            result.booking = {
                ...result.booking,
                traveller: {
                    hasMembershipCard: hasMembershipCardValue
                }
            };
            delete result.booking.hasMembershipCard;
        }


        const hasFareOptimisationValue: boolean = [
            'hasFareOptimisation','actionForFareOptimisation', 'savingMin', 'impactOnFareType', 'impactOnLuggage'
        ]
            .some((propertyName: string): boolean => Boolean(input[propertyName]));


        if (hasServiceValue) {
            result.booking = {
                ...result.booking,
                service: {
                    code: serviceCode,
                    xStatus: serviceXStatus,
                }
            };
        }

        if(input.hasSeatAssignment?.length){
            result.booking = {
                ...result.booking,
                service: {
                    ...result.booking?.service,
                    hasSeatAssignment: input.hasSeatAssignment[0] === 'true'
                }
            };
            delete result.booking.hasSeatAssignment;
        }

        if (hasPriceValue) {
            result.booking = {
                ...result.booking,
                price: {
                    rate: pricingRate,
                    xState: pricingXState,
                    fareBasis: pricingFarebasis,
                }
            };
        }

        if (seatStatus && seatStatus.length) {
            result.booking.seatStatus = seatStatus;
        }

        if (hasSectorValue) {
            result.booking = {
                ...result.booking,
                sector: {
                    matchAll: sectorMatchAll,
                    from: sectorFrom,
                    to: sectorTo,
                    seatStatus: sectorSeatStatus,
                    begin: sectorBeginDate,
                    end: sectorArrivalDate,
                    carrier: sectorCarrier,
                    operatingCarrier: sectorOperatingCarrier,
                    flightNumber: sectorFlightNumber,
                }
            };
        }

        if (hasTicketValue) {
            result.booking = {
                ...result.booking,
                ticket: {
                    matchAll: ticketMatchAll,
                    ticketNativeStatus: ticketNativeStatus,
                    sector: {
                        from: ticketSectorFrom,
                        to: ticketSectorTo,
                        begin: ticketSectorBeginDate,
                        carrier: ticketSectorCarrier,
                        ticketStatus: ticketStatus,
                        flight: ticketFlightNumber,
                    }
                }
            }
        }

        if (hasExternalBookingValue) {
            result.booking = {
                ...result.booking,
                externalBooking: {
                    xStatus: midOffice
                },
            };
        }

        if (hasDocumentValue) {
            result.booking = {
                ...result.booking,
                document: {
                    ticket: documentTicket,
                }
            };
        }

        if (hasRetentionValue) {
            result.booking.retention = {
                text: retentionReason,
                validity: whenRetentionEnds
            };
        }

        if (hasRemarkValue) {
            result.booking = {
                ...result.booking,
                remark: {
                    text: remarkText,
                    remark: remarkRemark,
                    note: remarkNote,
                    invoicing: remarkInvoicing,
                    osi: remarkOsi,
                    vendorAlert: remarkVendorAlert,
                }
            };
        }
        if (hasPropertyValue) {
            result.booking = {
                ...result.booking,
                property: {
                    name: propertyName,
                    code: propertyReference,
                    address: placesToStay,
                    begin: stayBeginDate,
                    end: stayArrivalDate,
                }
            };
        }

        if (hasPendingValue) {
            result.booking = {
                ...result.booking,
                pendingValue: {
                    min: pendingValueMin,
                    max: pendingValueMax,
                }
            };
        }
        if (hasPercentageValue) {
            result.booking = {
                ...result.booking,
                pendingPercentage: {
                    min: pendingPercentageMin,
                    max: pendingPercentageMax,
                }
            };
        }
        if (hasPaidValue) {
            result.booking = {
                ...result.booking,
                paidValue: {
                    min: paidValueMin,
                    max: paidValueMax,
                }
            };
        }
        if (hasPaidPercentage) {
            result.booking = {
                ...result.booking,
                paidPercentage: {
                    min: paidPercentageMin,
                    max: paidPercentageMax,
                }
            };
        }
        if (hasDueValue) {
            result.booking = {
                ...result.booking,
                dueValue: {
                    min: dueValueMin,
                    max: dueValueMax,
                }
            };
        }
        if (hasDuePercentage) {
            result.booking = {
                ...result.booking,
                duePercentage: {
                    min: duePercentageMin,
                    max: duePercentageMax,
                }
            };
        }
        if (hasQualityCheckValue) {
            result.booking = {
                ...result.booking,
                qualityCheck: {
                    controls: qualityCheck
                }
            };
        }
        if (hasFareOptimisationValue){
            result.booking = {
                ...result.booking,
                fareOptimisation: {
                    hasFareOptimisation: hasFareOptimisation,
                    fareOptimisationOfferAggregated : {
                        actionForFareOptimisation: actionForFareOptimisation,
                        impactOnFareType: impactOnFareType,
                        impactOnLuggage: impactOnLuggage,
                    }
                }
            };

            if(savingMin){
                result.booking.fareOptimisation.fareOptimisationOfferAggregated = {
                    ...result.booking.fareOptimisation.fareOptimisationOfferAggregated,
                    minimumSavingValue: {
                        min : savingMin*100
                    }
                };
            }
            if (result.booking.fareOptimisation.fareOptimisationOfferAggregated?.impactOnLuggage?.includes('Same')){
                let impactOnLuggageTemp : string[] = result.booking.fareOptimisation.fareOptimisationOfferAggregated.impactOnLuggage;
                impactOnLuggageTemp = [
                    ...impactOnLuggageTemp,
                    'LuggageInHold2LuggageInHold',
                    'NoLuggageInHold2NoLuggageInHold',
                    'LuggageWithSameRule'
                ];
                const sameIndex: number = impactOnLuggageTemp.findIndex((v: string): boolean => v === 'Same');
                impactOnLuggageTemp.splice(sameIndex, 1);
                result.booking.fareOptimisation.fareOptimisationOfferAggregated.impactOnLuggage = impactOnLuggageTemp;
            }
            if (result.booking.fareOptimisation.fareOptimisationOfferAggregated?.impactOnLuggage?.includes('Different')){
                let impactOnLuggageTemp : string[] = result.booking.fareOptimisation.fareOptimisationOfferAggregated.impactOnLuggage;
                impactOnLuggageTemp = [
                    ...impactOnLuggageTemp,
                    'LuggageInHold2NoLuggageInHold',
                    'NoLuggageInHold2LuggageInHold',
                    'Unknown2NoLuggageInHold',
                    'Unknown2LuggageInHold',
                    'LuggageInHold2Unknown',
                    'NoLuggageInHold2Unknown',
                    'LuggagePresentWithDifferentRule',
                    'Unknown2Unknown'
                ];
                const differentIndex: number = impactOnLuggageTemp.findIndex((v: string): boolean => v === 'Different');
                impactOnLuggageTemp.splice(differentIndex, 1);
                result.booking.fareOptimisation.fareOptimisationOfferAggregated.impactOnLuggage = [
                    ... new Set (impactOnLuggageTemp)
                ];
            }
            delete result.booking.hasFareOptimisation;
            delete result.booking.actionForFareOptimisation;
            delete result.booking.savingMin;
        }

        return result;
    }
}

export interface IApiBookingSearchParameters {
    syscode?: string[];
    code?: string[];
    host?: string[];
    agent?: string[];
    agency?: string[];
    brand?: IApiCodeTextSearchParameters;
    xStatus?: string[];
    externalBooking?: IApiExternalBooking;
    issueStatus?: string[];
    seatStatus?: string[];
    whenCreated?: IApiDateRange;
    begin?: IApiDateRange;
    end?: IApiDateRange;
    whenLastUpdated?: IApiDateRange;
    whenIssued?: IApiDateRange;
    whenToIssue?: IApiDateRange;
    dateLastCancellationWithoutPenalty?: IApiDateRange;
    option?: IApiDateRange;
    duration?: IApiDuration;
    location?: IApiCodeTextSearchParameters;
    city?: string;
    //property?: IApiProperty;
    person?: string[];
    traveller?:IApiTravellerParameters;
    hasMembershipCard?: string[];
    fromSearchParameters?: boolean;
    sector?: IApiSectorParameters;
    document?: IApiDocumentParameters;
    price?: IApiPriceParameters;
    service?: IApiServiceParameters;
    hasSeatAssignment?:string[];
    retention?: IApiBookingRetentionSearchParameters;
    remark?: IApiBookingRemarkSearchParameter;
    ticket?: IApiTicketSearchParameters;
    pendingValue?: IMinMax;
    percentageValue?: IMinMax;
    pendingPercentage?: IMinMax;
    paidValue?: IMinMax;
    paidPercentage?: IMinMax;
    dueValue?: IMinMax;
    duePercentage?: IMinMax;
    property?: IApiPropertySearchParameter;
    fareOptimisation?: IApiFareOptimisationSearchParameter;
    hasFareOptimisation?: boolean;
    actionForFareOptimisation?: string[];
    savingMin?: number;
    impactOnFareType?: string[];
    impactOnLuggage?: string[];
    qualityCheck?: IQualityCheckParameter;
}

interface IApiPropertySearchParameter {
    address?: string[];
    name?: string[];
    code?: string[];
    begin?: IApiDateRange;
    end?: IApiDateRange;
}

interface IQualityCheckParameter {
    controls?: string[];
}

interface IApiTicketSearchParameters {
    matchAll?: boolean;
    ticketNativeStatus?: string[];
    sector?: IApiTicketSectorSearchParameters;
}

interface IApiTicketSectorSearchParameters {
    begin?: IApiDateRange;
    from?: IApiCodeTextSearchParameters;
    to?: IApiCodeTextSearchParameters;
    carrier?: IApiCodeTextSearchParameters;
    flight?: string[];
    ticketStatus?: string[];
}

interface IApiBookingRetentionSearchParameters {
    text: string[];
    validity: IApiDateRange;
}

export interface IApiBookingRemarkSearchParameter {
    text?: string[];
    remark?: string[];
    note?: string[];
    invoicing?: string[];
    osi?: string[];
    vendorAlert?: string[];
}

export interface IApiServiceParameters {
    code: string[];
    xStatus: string[];
    hasSeatAssignment?: boolean;
}

export interface IApiPriceParameters {
    rate?: string[];
    xState?: string[];
    fareBasis?: string[];
}

export interface IApiDocumentParameters {
    ticket: string[];
}
export interface IApiTravellerParameters {
    hasMembershipCard?: boolean;
}

export interface IApiSectorParameters {
    matchAll?: boolean;
    from?: IApiCodeTextSearchParameters;
    to?: IApiCodeTextSearchParameters;
    seatStatus?: string[];
    begin?: IApiDateRange;
    end?: IApiDateRange;
    carrier?: IApiCodeTextSearchParameters;
    operatingCarrier?: IApiCodeTextSearchParameters;
    flightNumber?: string[];
}

export interface IApiExternalBooking {
    xStatus?: string[];
}

export interface IApiCodeTextSearchParameters {
    code?: string[];
    text?: string[];
}

export interface IApiDateRange {
    min?: string;
    max?: string;
}

export interface IApiDuration {
    night: number;
    day: number;
}

export interface IApiProperty {
    syscode: string[];
    text: string[];
}

export interface IApiFareOptimisationSearchParameter {
    hasFareOptimisation?: boolean;
    fareOptimisationOfferAggregated?:IApiFareOptimisationOfferAggregatedSearchParameter;
}

export interface IApiFareOptimisationOfferAggregatedSearchParameter {
    actionForFareOptimisation?: string[];
    minimumSavingValue?: IMinMax;
    impactOnFareType?: string[];
    impactOnLuggage?: string[];
}

export enum ApiPricingRate {
    Public = 'public',
    Negotiated = 'negotiated',
}

export enum ApiPricingXState {
    Guaranteed = 'guaranteed',
    Expired = 'expired',
    Invalid = 'invalid',
    NotGuaranted = 'notguaranteed',
    Issued = 'issued',
    Manual = 'manual',
}

export enum ApiPricingXTarget {
    total = 'total'
}

export enum ApiPricingXType {
    cost = 'cost',
    retail = 'retail',
}

export enum ApiPricingXRank {
    external = 'external',
    real = "real",
}

export enum ApiMidOffice {
    Confirm = 'confirm',
    Cancel = 'cancel',
    Option = 'option',
}

export enum ApiXType {
    Group = 'group',
    Individual = 'individual',
}

export enum ApiIssueStatus {
    // /!\ Values' order is preserved in the search form
    // Either keep the values in a logical order
    // or activate the alphabetical sorting of multiselect values in the form
    CompletelyIssued = 'completelyIssued',
    PartiallyIssued = 'partiallyIssued',
    NotIssued = 'notIssued',
}

export enum ApiServiceXStatus {
    Confirm = 'confirm',
    Request = 'request',
    NoAction = 'noaction',
    Unknown = 'unknown',
    Cancel = 'cancel',
    UnableToConfirm = 'unabletoconfirm',
    Failed = 'failed',
}


export enum ApiEligibility {
    Inhibited = 'inhibited',
    NotApplicable = 'notApplicable',
    Applicable = 'applicable',
    ApplicableNow = 'applicableNow',
    ApplicableLater = 'applicableLater',
}

export enum ApiActionForFareOptimisation {
    Rebook = 'REBOOK',
    Reprice = 'REPRICE',
    Split = 'SPLIT',
}

export enum ApiImpactOnLuggage {
    Same = 'Same', //Luggage2Luggage + No2No
    Different = 'Different', //Lugga2No + No2Lugge
    LuggageInHold2NoLuggageInHold = 'LuggageInHold2NoLuggageInHold',
    NoLuggageInHold2LuggageInHold = 'NoLuggageInHold2LuggageInHold',
}

export enum ApiImpactOnFareType {
    NegoFare2PublicFare = 'NegoFare2Public',
    Public2NegoFare = 'Public2NegoFare',
    Public2Public = 'Public2Public',
    NegoFare2NegoFare = 'NegoFare2NegoFare',
}

export enum ApiMembershipCard {
    True = 'true',
    False = 'false'
}

export enum ApiSeatAssignment {
    True = 'true',
    False = 'false'
}